/**
 * revenueByMonthView.ts — the pure half of the shared Revenue-by-month chart
 * (§24 C-8): the k-chart contract's numbers, the month labels, the per-bar
 * emphasis and the all-zero test. RevenueByMonthChart.tsx only draws this.
 */
import { moneyAxis, type MoneyAxis } from './chartAxis'

/** One wire bucket: "YYYY-MM" + closed-won value in integer PENCE. The server
 *  window is six months, zero-filled, ascending, ending on the CURRENT month
 *  (module.go generate_series to date_trunc('month', NOW()); the mock mirrors
 *  it; dashboardView.closedThisMonthPence already reads the last bucket as
 *  "this month" on the same ground). */
export interface RevenueMonth { month: string; wonPence: number }

/**
 * The S-Tier k-chart contract (src/styles/klaxiora.css .k-chart family,
 * docs/design/stier/README.md "Chart"), as recharts geometry:
 *   gutter        52 px  — .k-chart grid-template-columns: 52px …  → YAxis width
 *   plotHeight   216 px  — .k-chart-plot height                    → the plot
 *   axisFontSize 9.5 px  — .k-chart-axis (mono, --text3)           → tick font
 *   maxBarWidth   84 px  — .k-bar max-width                        → maxBarSize
 * plus the two values the contract's per-bar value label needs and recharts
 * adds outside the plot: the label's own headroom above the top tick (a bar
 * that reaches the top tick exactly — the mock's £8k does — must still carry
 * its label) and the category row under the baseline.
 */
export const K_CHART = {
  gutter: 52,
  plotHeight: 216,
  axisFontSize: 9.5,
  maxBarWidth: 84,
  labelFontSize: 10,
  labelOffset: 6,
  marginTop: 20,
  xAxisHeight: 30,
} as const

/** §24 C-5: the all-zero card says so instead of drawing five "£0" gridlines. */
export const REVENUE_EMPTY_MESSAGE = 'No closed-won revenue in the last 6 months'

// "2026-03" → "Mar" — fixed table, no locale/timezone parsing on a bare key
// (the same table both pages carried privately before C-8).
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export function monthLabel(key: string): string {
  return MONTH_ABBR[Number(key.slice(5, 7)) - 1] ?? key
}

export interface RevenueBarRow extends RevenueMonth {
  label: string
  /** §24 C-3: the CURRENT month — the window's last bucket — is the one bar in
   *  `--accent` ("the 'current period' chart bar. Nothing else", README);
   *  every other month is `--accentSoft`. */
  current: boolean
}

export interface RevenueChartModel {
  allZero: boolean
  rows: RevenueBarRow[]
  axis: MoneyAxis
}

export function revenueChartModel(data: RevenueMonth[]): RevenueChartModel {
  const axis = moneyAxis(data.map(m => m.wonPence), { pence: true })
  return {
    allZero: axis.allZero,
    rows: data.map((m, i) => ({ ...m, label: monthLabel(m.month), current: i === data.length - 1 })),
    axis,
  }
}

/** §24 C-4: a value label above every NON-ZERO bar — compact, in the AXIS
 *  unit (£2.02M over a £2,022,276.46 bar on an M axis; the exact figure is
 *  the tooltip's, C-9); a zero bar (no bar) carries none. Empty string =
 *  recharts renders no glyphs. */
export function barValueLabel(value: unknown, axis: MoneyAxis): string {
  const n = Number(value)
  return n > 0 ? axis.formatCompact(n) : ''
}
